import {Anchor, Box, createStyles} from '@mantine/core';
import React, {useState} from 'react';

import {isAnalysisTemplatesEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import RunAnalysisTemplateModal from 'analysis-tool/create-analysis-modal/RunAnalysisTemplateModal';
import BenchlingConfig from 'BenchlingConfig';
import DataCatalogItemDetails, {DataCatalogItemActionIcon} from 'data-catalog/DataCatalogItemDetails';
import DataCatalogOrigin from 'data-catalog/DataCatalogOrigin';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import {updateFile} from 'files/network/FileDAO';
import {isSupportedMimeType} from 'files/preview/FilePreviewConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {fileStatusDisplay} from 'instruments/connection-files/ConnectionFilesStatus';
import notifications from 'notifications';
import {renderFilePreview} from 'pipelines/files/ScratchFilePaneContent';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import {readableTime} from 'util/DateUtil';
import * as modals from 'util/Modals';
import {formatBytes} from 'util/NameUtil';
import removeNulls from 'util/removeNulls';
import {callV3Internal} from 'v3-internal-client';

import {FileViewQuery} from './__generated/FileViewQuery';
import FileVersionHistoryPanel, {FILE_VERSION_HISTORY_PANEL_WIDTH_PX} from './FileVersionHistoryPanel';

type File = ExtractByTypename<FileViewQuery['file'], 'FileNode'>;
type ScratchFile = File['scratchFile'];

interface FileDetailsProps {
  file: File;
  reload: () => void;
}

const useStyles = createStyles(() => ({
  detailsBesideVersionHistory: {
    '&&': {
      right: FILE_VERSION_HISTORY_PANEL_WIDTH_PX,
      width: 'auto',
    },
  },
  versionHistoryPanel: {
    bottom: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  filePreview: {
    paddingTop: 12,
    height: '80vh',
    minHeight: 400,
  },
}));

async function downloadScratchFile(scratchFile: ScratchFile, fileId: string): Promise<void> {
  if (scratchFile.externalFileId == null || scratchFile.downloadUrl == null) {
    return;
  }
  const signedURL = await performSignedFileQuery(scratchFile.externalFileId, scratchFile.downloadUrl, fileId);
  if (signedURL) {
    window.open(signedURL, '_blank', 'noopener noreferrer');
  }
}

export default function FileDetails({file, reload}: FileDetailsProps): JSX.Element {
  const {classes} = useStyles();
  // null follows the file's current version, including after a revert publishes a new one.
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [isReverting, setIsReverting] = useState(false);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const isVersionHistoryEnabled = BenchlingConfig.PIPELINES_ENABLE_FILE_VERSION_HISTORY;
  const currentVersionId = file.currentVersionId ?? null;
  const viewableVersions = isVersionHistoryEnabled
    ? file.versions.filter((version) => version.scratchFile != null)
    : [];
  const selectedVersion = viewableVersions.find(
    (version) => version.id === (selectedVersionId ?? currentVersionId)
  );
  const isViewingPastVersion = selectedVersion != null && selectedVersion.id !== currentVersionId;
  const hasVersionHistory = selectedVersion != null && viewableVersions.length > 1;
  const isVersionHistoryShown = hasVersionHistory && isVersionHistoryOpen;
  const scratchFile = selectedVersion?.scratchFile ?? file.scratchFile;

  const filePreview = renderFilePreview(scratchFile);
  const hasPreviewableContent = isSupportedMimeType(scratchFile.mimeType, scratchFile.filename);

  const viewLatestVersion = (): void => {
    setSelectedVersionId(null);
  };

  const selectVersion = (versionId: string): void => {
    setSelectedVersionId(versionId === currentVersionId ? null : versionId);
  };

  const downloadVersion = (versionId: string): void => {
    const version = viewableVersions.find(({id}) => id === versionId);
    if (version?.scratchFile != null) {
      downloadScratchFile(version.scratchFile, file.id);
    }
  };

  const revertToVersion = async (versionId: string): Promise<void> => {
    const targetVersion = viewableVersions.find(({id}) => id === versionId);
    if (targetVersion == null || currentVersionId == null) {
      return;
    }
    const confirmed = await modals.confirm(
      `Revert to version ${targetVersion.versionNumber}?`,
      `This publishes a new version of the file with the contents of version ${targetVersion.versionNumber}. Earlier versions stay in the file's history.`,
      ButtonStyle.PRIMARY,
      {acceptText: 'Revert', rejectText: 'Cancel', enableRedesign: true, promiseCompatible: true}
    );
    if (!confirmed) {
      return;
    }
    setIsReverting(true);
    await callV3Internal('File.Revert', {
      pathParams: {file_id: file.id},
      body: {fileVersionId: targetVersion.id, expectedVersionId: currentVersionId},
    })
      .then(
        () => {
          setSelectedVersionId(null);
          reload();
          notifications.success(`Reverted to version ${targetVersion.versionNumber}.`);
        },
        (res: Response) => {
          notifications.notifyError(res);
        }
      )
      .finally(() => {
        setIsReverting(false);
      });
  };

  const extraColumns = [
    {label: 'Status', value: fileStatusDisplay(file, false)},
    {label: 'Benchling File ID', value: file.displayId},
    {label: 'Size', value: scratchFile.size ? formatBytes(scratchFile.size) : ''},
    {
      label: 'Origin',
      value: <DataCatalogOrigin originType={file.originType} lineageParent={file.lineageParent} />,
    },
    {label: 'Original File Name', value: scratchFile.filename},
    ...(hasVersionHistory
      ? [
          {
            label: 'Version',
            value: (
              <Anchor
                component="button"
                type="button"
                onClick={() => {
                  setIsVersionHistoryOpen(true);
                }}
              >
                {selectedVersion.id === currentVersionId
                  ? `Version ${selectedVersion.versionNumber} (current)`
                  : `Version ${selectedVersion.versionNumber}`}
              </Anchor>
            ),
          },
          {
            label: 'Version Published',
            value: selectedVersion.creator
              ? `${readableTime(selectedVersion.createdAt)} by ${selectedVersion.creator.name}`
              : readableTime(selectedVersion.createdAt),
          },
        ]
      : []),
  ];

  const actionIcons: Array<DataCatalogItemActionIcon | null> = [
    isAnalysisTemplatesEnabled() && BenchlingConfig.INSTRUMENTS_ENABLE
      ? {
          title: 'Run automation template',
          icon: 'analytics_analysis',
          onClick: () =>
            legacyRunModal(RunAnalysisTemplateModal, {
              variableDefaults: {FILE: file.id},
              initialAnalysisName: `Analyze ${file.name}`,
            }),
        }
      : null,
    hasVersionHistory
      ? {
          title: isVersionHistoryShown ? 'Hide version history' : 'Show version history',
          icon: 'history',
          onClick: () => {
            setIsVersionHistoryOpen(!isVersionHistoryShown);
          },
        }
      : null,
    isViewingPastVersion && file.permissions.writable
      ? {
          title: 'Revert to this version',
          icon: 'undo',
          disabled: isReverting,
          onClick: () => revertToVersion(selectedVersion.id),
        }
      : null,
    {
      title: 'Download original file',
      icon: 'download',
      disabled: scratchFile.externalFileId == null || scratchFile.downloadUrl == null,
      onClick: () => downloadScratchFile(scratchFile, file.id),
    },
  ];

  const details = (
    <DataCatalogItemDetails
      className={isVersionHistoryShown ? classes.detailsBesideVersionHistory : undefined}
      dataCatalogItem={file}
      extraColumns={extraColumns}
      metadataTitle="File Metadata"
      tagsEmptyMessage="This file has no custom fields."
      onChange={async (updateParams: {
        id: string;
        name?: string;
        folderId?: string;
        customFields?: {[name: string]: string};
      }): Promise<void> => {
        await updateFile(updateParams);
        reload();
      }}
      actionIcons={removeNulls(actionIcons)}
      hasContent={hasPreviewableContent}
      preferenceKey={`file:${scratchFile.mimeType ?? 'unknown'}`}
    >
      {isViewingPastVersion && (
        <Box pt="xs">
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            supportingInfo={
              <span>
                You&apos;re viewing a previous file version.{' '}
                <Anchor component="button" type="button" onClick={viewLatestVersion}>
                  View the latest version.
                </Anchor>
              </span>
            }
          />
        </Box>
      )}
      <div className={classes.filePreview}>{filePreview}</div>
    </DataCatalogItemDetails>
  );

  if (!isVersionHistoryShown) {
    return details;
  }
  return (
    <>
      {details}
      <FileVersionHistoryPanel
        className={classes.versionHistoryPanel}
        currentVersionId={currentVersionId}
        onClose={() => {
          setIsVersionHistoryOpen(false);
        }}
        onDownload={downloadVersion}
        onRevert={file.permissions.writable && !isReverting ? revertToVersion : undefined}
        onSelectVersion={selectVersion}
        selectedVersionId={selectedVersion.id}
        versions={viewableVersions}
      />
    </>
  );
}
